import { api, types } from "api";
// import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, styled, Tab, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GET_RESULT_FAIL } from "../../messages";
import { useAuthStatus } from "../../helpers/useAuthStatus";
import { ColorTabs } from "components/ColorTabs";
import { GroupTable } from "./GroupTable";
import { routes } from "routes";
import { Link, useLocation } from "react-router-dom";
import { DownloadDialog } from "../../components/DownloadDialog/DownloadDialog";
import {
	GroupResultProps,
	MultiGroupResultProps,
	RoleType,
	UUID,
} from "../../api/types";

const PREFIX = "GroupTablePage";

const classes = {
	root: `${PREFIX}-root`,
	tabs: `${PREFIX}-tabs`,
	tabsBarBox: `${PREFIX}-tabsBarBox`,
	button: `${PREFIX}-button`,
	buttonList: `${PREFIX}-buttonList`,

	appBar: `${PREFIX}-appBar`,
	indicator: `${PREFIX}-indicator`,
	table: `${PREFIX}-table`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
	},
	[`& .${classes.tabs}`]: {
		color: "white",
	},
	[`& .${classes.tabsBarBox}`]: {
		backgroundColor: theme.palette.primary.main,
	},
	[`& .${classes.button}`]: {
		minWidth: theme.spacing(21),
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	[`& .${classes.buttonList}`]: {
		display: "flex",
	},

	[`& .${classes.appBar}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.table}`]: {
		marginTop: theme.spacing(4),
	},
}));

interface stateType {
	groups: types.Group[];
}

export const GroupTablePage = (): JSX.Element => {
	const { role } = useAuthStatus();
	const [groupName, setGroupName] = useState("");
	// const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [, setErr] = useState("");
	const [groups, setGroups] = useState<types.Group[]>([]);
	const [allGroups, setAllGroups] = useState<types.Group[]>([]);
	const [getResults, setGetResults] = useState<UUID[]>([]);
	const [loading, setLoading] = useState(true);
	const [groupResultPropsList, setGroupResultPropsList] =
		useState<MultiGroupResultProps>({
			propsList: [],
		});
	const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
	const location = useLocation<stateType>();

	const fetchGroups = useCallback(() => {
		api
			.getGroups()
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				if (res.data === null || res.data.length === 0) {
					setGroups([]);
					return;
				}

				setGroups(res.data);
				setAllGroups(res.data);
			})
			.catch(() => {
				setErr(GET_RESULT_FAIL);
			});
	}, []);

	const fetchMultiGroupResults = useCallback(() => {
		if (getResults.length === 0) {
			return;
		}

		api
			.getMultiGroupResults(getResults)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				let resPropsList: GroupResultProps[] = [];
				res.data.forEach((groupRes) => {
					const resultProps: GroupResultProps = {
						name: groupRes.name,
						date: groupRes.date,
						yearLevel: String(groupRes.yearLevel),
						totalAssignment: String(groupRes.totalAssignment),
						totalCorrect: String(groupRes.totalCorrect),
						totalAnswered: String(groupRes.totalAnswered),
						groupAssignmentResults: groupRes.groupAssignmentResultList,
						curriculumResults: groupRes.curriculumResultList,
						comments: groupRes.commentList,
						id: groupRes.groupId,
					};
					resPropsList = [...resPropsList, resultProps];
				});
				setGroupResultPropsList({ propsList: resPropsList });
				setLoading(false);
				setOpenDownloadDialog(true);
			})
			.catch(() => setErr(GET_RESULT_FAIL));
	}, [getResults]);

	useEffect(() => {
		if (location.state === undefined) {
			if (role === "admin" || role === "teacher" || role === "admin-teacher") {
				fetchGroups();
			}
		} else {
			setGroups(location.state.groups);
		}
	}, [role, fetchGroups, location]);

	useEffect(() => {
		if (role === "admin" || role === "teacher" || role === "admin-teacher") {
			fetchMultiGroupResults();
		}
	}, [role, fetchMultiGroupResults]);

	const onDownload = (ids: string[]): void => {
		setGetResults(ids);
	};

	const onCheckOpen = (open: boolean): void => {
		setOpenDownloadDialog(open);
	};

	return (
		<Root className={classes.root}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				className={classes.tabsBarBox}
			>
				<ColorTabs
					value={routes._.result._.Groups.path}
					variant="scrollable"
					scrollButtons
				>
					<Tab
						label="GROUP LIST"
						value={routes._.result._.Groups.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.result._.Groups.path}
					/>
					<Tab
						label="STUDENT LIST"
						value={routes._.result._.Students.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.result._.Students.path}
					/>
				</ColorTabs>
			</Box>

			<GroupTable groups={groups} onDownload={onDownload}>
				<Grid item xs={1}>
					{!loading && (
						<DownloadDialog
							role={role as RoleType}
							loading={false}
							resultToBeRendered={groupResultPropsList}
							templateType={"group"}
							checkOpen={openDownloadDialog}
							onCheckOpen={onCheckOpen}
						/>
					)}
				</Grid>
				<Grid item xs={4}>
					<TextField
						id="group-name"
						label="Group Name"
						variant="outlined"
						value={groupName}
						onChange={(e) => {
							const searchName = e.target.value;
							if (searchName.length < groupName.length) {
								// monitor backspace
								setGroups(allGroups);
							} else {
								let searchGroups: Array<types.Group> = [];
								const regex = /^ *$/;
								// a name is null or empty string or all whitespace
								if (regex.exec(searchName) !== null) {
									setGroups(allGroups);
								} else {
									groups.forEach((group) => {
										if (
											group.name
												.toLowerCase()
												.includes(searchName.trim().toLowerCase())
										) {
											searchGroups = [...searchGroups, group];
										}
									});
									setGroups(searchGroups);
								}
							}
							setGroupName(searchName);
						}}
						fullWidth
						InputProps={{
							endAdornment: <SearchIcon />,
						}}
					/>
				</Grid>
			</GroupTable>
		</Root>
	);
};
